/* eslint-disable @typescript-eslint/no-explicit-any */
import { GetServerSideProps } from 'next';
import { getProcessEnvs, PageRendererComponent, prePageCall } from '@marriott/mi-headless-utils';
import { mockModel } from '../../mocks/mockModel';
import { FEATURE_FLAGS, fetchFlag, TARGETS } from '@marriott/mi-feature-flag-utils';
import {
  getRegionDataToStore,
  CACHE_TTL_SECONDS,
  fetchDocumentIdFromRoutingConfig,
  fetchModelFromCouchbase,
  updateModelInCouchbase,
  fetchCategoryPropertyCount,
  areFiltersEqual,
  extractSubDirectoryContextPath,
} from '@marriott/mi-category-components';
import { envVarKeys } from '../../_constants/envVarKeys';
import signatures from '../../signatures/categorySignatures.json';

function pageRenderer(props: any) {
  return <PageRendererComponent {...props} />;
}

const allInclusiveSpecificData = async () => {
  const { GOOGLE_MAP_API_KEY } = getProcessEnvs();
  const [featureFlagLogo, featureFlagAemltsHost] = await Promise.all([
    fetchFlag(FEATURE_FLAGS.EXPERIENCE_MCOM_LOGO_AUTOMATION, {
      target: TARGETS.APP.CATEGORY_RENDERER,
    }),
    fetchFlag(FEATURE_FLAGS.EXPERIENCE_MCOM_CATEGORY_RENDERER_AEMLTS_HOST, {}),
  ]);
  const useAemLtsHost = featureFlagAemltsHost?.experience_mcom_category_renderer_aemlts_host;
  const aemHost = useAemLtsHost ? process.env.NEXT_PUBLIC_AEM_LTS_HOST : process.env.NEXT_PUBLIC_AEM_HOST;
  return {
    operationSignatures: signatures,
    featureFlags: { featureFlagLogo },
    featureFlagAemltsHost: featureFlagAemltsHost ?? null,
    aemHost: aemHost ?? null,
    nextPublicEnvKeys: [...Object.keys(envVarKeys)],
    GOOGLE_MAP_API_KEY,
    serverDataLayer: {},
  };
};

const postAEMModelOperations = async (...args: any[]) => {
  const [context, , , model] = args;
  const sessionID = context?.req?.cookies?.sessionID ?? '';
  const requestId = context?.req?.headers?.['x-request-id'] ?? '';
  const acceptLanguage = context?.req?.headers?.['accept-language']?.split(',')?.[0] ?? model?.language ?? 'en-US';
  const pagePath = context?.resolvedUrl?.split('?')[0] ?? '/ways-to-stay';
  const locale = model?.language ?? 'en-US';

  const { CATEGORY_APP_DOMAIN, MI_DATASTORE_APP_URL, HYBRID_CONFIG_URL } = getProcessEnvs();
  const initialDestinationMetaData = model?.destinationMetaData ?? {};
  const categoryStayTypesItems = model.cqItems?.root?.[':items']?.responsivegrid?.[':items'];

  // Extract minPropertyCount from page-level properties (AEM page properties)
  const minPropertyCount = model?.minPropertyCount ?? 2;

  // DEBUG: Log minPropertyCount source
  console.log('[CategoryDebug] minPropertyCount check:', {
    fromAEM: model?.minPropertyCount,
    usingValue: minPropertyCount,
    isFromAEM: model?.minPropertyCount !== undefined,
    pagePath,
  });

  // Extract component data from model
  let categoryStayTypes: any;
  let componentDestinationMetaData: any;
  let categorylinklistFilters: any;
  let subRegionData: any;
  let mapRegionData: any;
  let viewAllText: string | undefined;

  if (categoryStayTypesItems) {
    Object.keys(categoryStayTypesItems).forEach(key => {
      const component = categoryStayTypesItems[key];
      if (component[':type'] === 'mi-aem-category-spa/components/categorypropertyresultscontainer') {
        categoryStayTypes = component?.categoryStayTypes;
      }
      if (
        component[':type'] === 'mi-aem-category-spa/components/categorydynamicpropertycards' &&
        component?.destinationMetaData &&
        Object.keys(component.destinationMetaData).length > 0
      ) {
        componentDestinationMetaData = component.destinationMetaData;
      }
      if (component[':type'] === 'mi-aem-category-spa/components/categorylinklist') {
        categorylinklistFilters = component?.categoryFilters;
        subRegionData = component?.subRegion;
        mapRegionData = component?.mapRegion;
        viewAllText = component?.viewAllText;
        console.log('[CategoryDebug] Found categorylinklist component:', {
          hasSubRegion: !!subRegionData,
          subRegionType: typeof subRegionData,
          subRegionLength: typeof subRegionData === 'string' ? subRegionData.length : 'N/A',
          hasMapRegion: !!mapRegionData,
          viewAllText: viewAllText,
        });
      }
    });
  }

  // DEBUG: Log extracted data
  console.log('[CategoryDebug] Extracted from AEM model:', {
    hasSubRegionData: !!subRegionData,
    subRegionDataType: typeof subRegionData,
    hasCategoryStayTypesItems: !!categoryStayTypesItems,
    componentKeys: categoryStayTypesItems ? Object.keys(categoryStayTypesItems) : [],
  });

  // Fallback: check if component level facets are not available then assign page(model) level facets.
  if (!categoryStayTypes || Object.keys(categoryStayTypes).length === 0) {
    categoryStayTypes = model?.categoryStayTypes;
  }

  // Get pageDocId from hybrid-config
  const pageDocId = await fetchDocumentIdFromRoutingConfig(HYBRID_CONFIG_URL, pagePath, locale, sessionID, requestId);

  let keywordSearchTotal = 0;
  let regionDataToStore: any = {};

  // Helper to fetch property count
  const fetchPropertyCount = (stayTypes: any) =>
    fetchCategoryPropertyCount(CATEGORY_APP_DOMAIN, stayTypes, sessionID, requestId, acceptLanguage);

  if (MI_DATASTORE_APP_URL && pageDocId) {
    const cachedModel = await fetchModelFromCouchbase(MI_DATASTORE_APP_URL, pageDocId, requestId);

    const hasCachedKeywordSearchTotal =
      cachedModel && cachedModel.keywordSearchTotal !== undefined && Number(cachedModel.keywordSearchTotal) > 0;
    const hasCachedRegionDataToStore =
      cachedModel &&
      cachedModel.regionDataToStore &&
      typeof cachedModel.regionDataToStore === 'object' &&
      Object.keys(cachedModel.regionDataToStore as object).length > 0;

    if (hasCachedKeywordSearchTotal && hasCachedRegionDataToStore) {
      // Cache hit
      console.log('[CategoryDebug] CACHE HIT:', {
        pageDocId,
        keywordSearchTotal: cachedModel.keywordSearchTotal,
        regionDataToStoreKeys: Object.keys(cachedModel.regionDataToStore as object),
      });
      keywordSearchTotal = cachedModel.keywordSearchTotal as number;
      regionDataToStore = cachedModel.regionDataToStore as any;
    } else {
      // Cache miss - fetch from API
      console.log('[CategoryDebug] CACHE MISS:', {
        pageDocId,
        hasCachedKeywordSearchTotal,
        hasCachedRegionDataToStore,
        reason: !hasCachedKeywordSearchTotal ? 'missing keywordSearchTotal' : 'missing regionDataToStore',
      });
      const hasCategorylinklistFilters = categorylinklistFilters && Object.keys(categorylinklistFilters).length > 0;
      const filtersMatch = areFiltersEqual(categorylinklistFilters, categoryStayTypes);

      let keywordSearchFacets: any[] = [];

      if (hasCategorylinklistFilters && !filtersMatch) {
        const [existingResult, linklistResult] = await Promise.all([
          fetchPropertyCount(categoryStayTypes),
          fetchPropertyCount(categorylinklistFilters),
        ]);
        keywordSearchTotal = existingResult.total;
        keywordSearchFacets = linklistResult.facets;
      } else {
        const { total: fetchedTotal, facets: fetchedFacets } = await fetchPropertyCount(categoryStayTypes);
        keywordSearchTotal = fetchedTotal;
        keywordSearchFacets = fetchedFacets;
      }

      regionDataToStore = getRegionDataToStore(
        subRegionData,
        minPropertyCount,
        mapRegionData,
        viewAllText,
        keywordSearchFacets
      );

      // Update cache
      if (keywordSearchTotal > 0 || Object.keys(regionDataToStore).length > 0) {
        const fieldsToUpdate = { keywordSearchTotal, regionDataToStore };
        await updateModelInCouchbase(
          MI_DATASTORE_APP_URL,
          pageDocId,
          model,
          fieldsToUpdate,
          CACHE_TTL_SECONDS,
          requestId
        );
      }
    }
  } else {
    // No pageDocId or datastore URL - fallback to API call without caching
    const hasCategorylinklistFilters = categorylinklistFilters && Object.keys(categorylinklistFilters).length > 0;
    const filtersMatch = areFiltersEqual(categorylinklistFilters, categoryStayTypes);

    let keywordSearchFacets: any[] = [];

    if (hasCategorylinklistFilters && !filtersMatch) {
      const [existingResult, linklistResult] = await Promise.all([
        fetchPropertyCount(categoryStayTypes),
        fetchPropertyCount(categorylinklistFilters),
      ]);
      keywordSearchTotal = existingResult.total;
      keywordSearchFacets = linklistResult.facets;
    } else {
      const { total: fetchedTotal, facets: fetchedFacets } = await fetchPropertyCount(categoryStayTypes);
      keywordSearchTotal = fetchedTotal;
      keywordSearchFacets = fetchedFacets;
    }

    regionDataToStore = getRegionDataToStore(
      subRegionData,
      minPropertyCount,
      mapRegionData,
      viewAllText,
      keywordSearchFacets
    );
  }

  // Enrich model with computed values
  const items = model.cqItems?.root?.[':items']?.responsivegrid?.[':items'];
  const subDirectoryContextPath = extractSubDirectoryContextPath(
    context?.resolvedUrl,
    model?.subDirectoryContextPath,
    model?.language
  );

  if (items) {
    Object.keys(items).forEach(key => {
      const component = items[key];

      if (component[':type'] === 'mi-aem-category-spa/components/categorypropertyresultscontainer') {
        component.keywordSearchTotal = keywordSearchTotal;
        component.isCategoryRenderer = true;
        component.operationSignatures = signatures;
      }

      if (component[':type'] === 'mi-aem-category-spa/components/categorylinklist') {
        component.sectionList = regionDataToStore;
      }
    });
  }

  const resolvedDestinationMetaData =
    Object.keys(initialDestinationMetaData).length > 0
      ? initialDestinationMetaData
      : componentDestinationMetaData ?? {};

  model.subDirectoryContextPath = subDirectoryContextPath;
  model.keywordSearchTotal = keywordSearchTotal;
  model.destinationMetaData = resolvedDestinationMetaData;

  return {
    ...model,
    subDirectoryContextPath,
    ENABLE_ACDL: false,
  };
};

export const getServerSideProps: GetServerSideProps<any> = prePageCall(
  allInclusiveSpecificData,
  mockModel,
  false,
  false,
  false,
  undefined,
  postAEMModelOperations
);

export default pageRenderer;
